'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, enqueueSnackbar, Select, TextField, useThemeMode } from '@bomdisoft/components';
import { cancelText, continueText, getLanguageCode, getLanguageFromLocale, nameText, Translation, tryAgain } from '@bomdisoft/translations';
import { formatMoney } from '@bomdisoft/utilities';
import s from '@pieleaf/app/[lang]/orders/styles.module.css';
import { placeOrder, publicGraphql } from '@pieleaf/database/pieleaf-api';
import {
  agencyText,
  contactText,
  deliveryText,
  enterMomoNumberText,
  enterNameText,
  enterPaypalIDText,
  enterPhoneText,
  maxOrderQuantityText,
  minOrderQuantityText,
  momoNumberText,
  mtnMobileMoney,
  navOrders,
  orderTotalText,
  paymentMethodIconAlt,
  paymentMethodText,
  paymentText,
  paypalIDText,
  phoneText,
  placeOrderText,
  productText,
  quantityText,
  selectAgencyText,
  selectPaymentMethodText,
  selectProductText,
  selectTownText,
  successText,
  townText,
} from '@pieleaf/translations';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';

const paymentMethods: PaymentMethod[] = [{ icon: '/images/mtn.jpeg', id: 'MTN MoMo', name: mtnMobileMoney }];

type PaymentMethod = {
  id: PaymentProcessorName;
  name: Translation;
  icon: string;
};

const LabelDivider = ({ children }: { children: ReactNode }) => (
  <div className={s.labelDivider}>
    <hr className={s.labelDividerLine} />
    <span className={s.labelDividerText}>{children}</span>
    <hr className={s.labelDividerTrail} />
  </div>
);

export type PlaceOrderButtonProps = {
  className?: string;
};

export const PlaceOrderButton = ({ className }: PlaceOrderButtonProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(params.lang as string);
  const languageCode = getLanguageCode(language);
  const { resolvedMode = 'light' } = useThemeMode();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [momoNumber, setMomoNumber] = useState('');
  const [paypalID, setPaypalID] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [products, setProducts] = useState<Product[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [towns, setTowns] = useState<Town[]>([]);
  const [town, setTown] = useState<Town | null>(null);
  const [agency, setAgency] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(paymentMethods[0]);
  const [placedOrder, setPlacedOrder] = useState<{ id: string; total_sum: string } | null>(null);

  useEffect(() => {
    const getProducts = async () => {
      try {
        const { products: productsData } = await publicGraphql<{ products: Product[] }>('{ products { id name unitName unitPrice minOrderQuantity maxOrderQuantity } }');
        setProducts(productsData);
        if (!product) setProduct(productsData[0]);
      } catch {
        enqueueSnackbar(tryAgain[language], { variant: 'error' });
      }
    };

    // Only active towns come back: the server decides where it delivers, and
    // it rejects an order to an inactive town regardless of what this list says.
    const getTowns = async () => {
      try {
        const { towns: townsData } = await publicGraphql<{ towns: Town[] }>('{ towns { id name agencies fee } }');
        setTowns(townsData);
        if (!town) {
          setTown(townsData[0]);
          setAgency(townsData[0].agencies[0]);
        }
      } catch {
        enqueueSnackbar(tryAgain[language], { variant: 'error' });
      }
    };

    getProducts();
    getTowns();
  }, [product, town, language]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleCloseConfirmation = () => setPlacedOrder(null);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => setName(event.target.value);
  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => setPhone(event.target.value);
  const handleMomoNumberChange = (event: ChangeEvent<HTMLInputElement>) => setMomoNumber(event.target.value);
  const handlePaypalIDChange = (event: ChangeEvent<HTMLInputElement>) => setPaypalID(event.target.value);
  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>) => setQuantity(Number(event.target.value));

  const handleProductChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selected = products.find(candidate => candidate.id === event.target.value);
    if (!selected) return;
    setProduct(selected);
    if (selected.minOrderQuantity && selected.minOrderQuantity > quantity) setQuantity(selected.minOrderQuantity);
  };

  const handleTownChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selected = towns.find(candidate => candidate.id === event.target.value);
    if (!selected) return;
    setTown(selected);
    if (!agency || !selected.agencies.includes(agency)) setAgency(selected.agencies[0]);
  };

  const handleAgencyChange = (event: ChangeEvent<HTMLSelectElement>) => setAgency(event.target.value);
  const handlePaymentMethodChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selected = paymentMethods.find(candidate => candidate.id === event.target.value);
    if (selected) setPaymentMethod(selected);
  };

  // An ESTIMATE, shown so the buyer knows roughly what they will pay. The server
  // recomputes the real total from the current price and town fee and returns
  // it; this figure is never sent and never trusted.
  const estimatedTotal = useMemo(() => {
    if (!product || !town) return 0;
    return product.unitPrice * quantity + town.fees;
  }, [product, quantity, town]);

  const handleOrder = async () => {
    if (!product) return enqueueSnackbar(selectProductText[language], { variant: 'error' });
    if (!name) return enqueueSnackbar(enterNameText[language], { variant: 'error' });
    if (!phone) return enqueueSnackbar(enterPhoneText[language], { variant: 'error' });
    if (!paymentMethod) return enqueueSnackbar(selectPaymentMethodText[language], { variant: 'error' });
    if (paymentMethod.id === 'Paypal' && !paypalID) return enqueueSnackbar(enterPaypalIDText[language], { variant: 'error' });
    if (['MTN MoMo', 'Orange MoMo'].includes(paymentMethod.id) && !momoNumber) return enqueueSnackbar(enterMomoNumberText[language], { variant: 'error' });
    if (!town) return enqueueSnackbar(selectTownText[language], { variant: 'error' });
    if (!agency) return enqueueSnackbar(selectAgencyText[language], { variant: 'error' });
    if (quantity < (product.minOrderQuantity || 0)) return enqueueSnackbar(minOrderQuantityText[language], { variant: 'error' });
    if (quantity > (product.maxOrderQuantity || 1000000)) return enqueueSnackbar(maxOrderQuantityText[language], { variant: 'error' });

    try {
      // Guest checkout: no session needed. The server stamps the buyer when one
      // is signed in, computes the total, and queues the confirmation SMS.
      const placed = await placeOrder({
        agency,
        contactName: name,
        contactPhone: phone,
        payerHint: paymentMethod.id === 'Paypal' ? paypalID : momoNumber || undefined,
        processor: paymentMethod.id,
        productId: Number(product.id),
        quantity,
        townId: Number(town.id),
      });

      setPlacedOrder(placed);
      enqueueSnackbar(successText[language], { variant: 'success' });
      setOpen(false);
    } catch (error) {
      if (!process.env.NEXT_PUBLIC_IS_PROD) console.error(error);
      enqueueSnackbar(error instanceof Error ? error.message : tryAgain[language], { variant: 'error' });
    }
  };

  return (
    <>
      <Button aria-label={placeOrderText[language]} className={className} color={resolvedMode === 'light' ? 'error' : 'info'} data-prodantix='Start an order' data-prodantix-action='place_order' onClick={handleOpen} variant='contained'>
        {placeOrderText[language]}
      </Button>
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle>{placeOrderText[language]}</DialogTitle>
        <DialogContent className={s.dialogContent}>
          <Select aria-label={productText[language]} className={s.field} id='order-product' label={productText[language]} name='product' onChange={handleProductChange} required value={product?.id ?? ''}>
            {products.map(candidate => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name[languageCode]}
              </option>
            ))}
          </Select>
          <TextField
            aria-label={quantityText[language]}
            className={s.field}
            endAdornment={product?.unitName.toLowerCase()}
            id='order-quantity'
            label={quantityText[language]}
            max={product?.maxOrderQuantity || 1000000}
            min={product?.minOrderQuantity || 0}
            name='quantity'
            onChange={handleQuantityChange}
            required
            type='number'
            value={quantity}
          />

          <LabelDivider>{deliveryText[language]}</LabelDivider>
          <Select aria-label={townText[language]} className={s.field} id='order-town' label={townText[language]} name='town' onChange={handleTownChange} required value={town?.id ?? ''}>
            {towns.map(candidate => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </Select>
          <Select aria-label={agencyText[language]} className={s.field} id='order-agency' label={agencyText[language]} name='agency' onChange={handleAgencyChange} required value={agency ?? ''}>
            {town?.agencies.map(candidate => (
              <option key={candidate} value={candidate}>
                {candidate}
              </option>
            ))}
          </Select>

          <LabelDivider>{contactText[language]}</LabelDivider>
          <TextField aria-label={nameText[language]} className={s.field} id='order-name' label={nameText[language]} name='name' onChange={handleNameChange} required value={name} />
          <TextField aria-label={phoneText[language]} className={s.field} id='order-phone' label={phoneText[language]} name='phone' onChange={handlePhoneChange} required value={phone} />

          <LabelDivider>{paymentText[language]}</LabelDivider>
          <div className={s.paymentRow}>
            <Image alt={`${paymentMethod.name[language]} ${paymentMethodIconAlt[language]}`} className={s.paymentIcon} height={20} src={paymentMethod.icon} width={20} />
            <Select aria-label={paymentMethodText[language]} className={s.field} id='order-payment-method' label={paymentMethodText[language]} name='paymentMethod' onChange={handlePaymentMethodChange} required value={paymentMethod.id}>
              {paymentMethods.map(pm => (
                <option key={pm.id} value={pm.id}>
                  {pm.name[language]}
                </option>
              ))}
            </Select>
          </div>
          {paymentMethod.id === 'Paypal' && <TextField aria-label={paypalIDText[language]} className={s.field} id='order-paypal-id' label={paypalIDText[language]} name='paypalID' onChange={handlePaypalIDChange} value={paypalID} />}
          {['MTN MoMo', 'Orange MoMo'].includes(paymentMethod.id) && <TextField aria-label={momoNumberText[language]} className={s.field} id='order-momo-number' label={momoNumberText[language]} name='momoNumber' onChange={handleMomoNumberChange} value={momoNumber} />}

          <LabelDivider>{orderTotalText[language]}</LabelDivider>
          <p className={s.estimatedTotal}>{estimatedTotal ? formatMoney(estimatedTotal) : '0'}</p>
        </DialogContent>
        <Divider />
        <DialogActions className={s.actions}>
          <Button aria-label={cancelText[language]} color='error' data-prodantix='Cancel the order' onClick={handleClose} variant='text'>
            {cancelText[language]}
          </Button>
          <Button aria-label={continueText[language]} data-prodantix='Confirm the order' data-prodantix-action='confirm_order' onClick={handleOrder} variant='contained'>
            {continueText[language]}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Shows the order details and id the source left unbuilt. The number and
          the phone are together the only way to track a guest order, so the
          buyer has to be shown it: a snackbar alone would vanish before they
          wrote it down. */}
      <Dialog data-testid='order-placed-dialog' onClose={handleCloseConfirmation} open={placedOrder !== null}>
        <DialogTitle>{successText[language]}</DialogTitle>
        <DialogContent className={s.dialogContent}>
          <LabelDivider>{navOrders[language]}</LabelDivider>
          <p className={s.estimatedTotal} data-testid='placed-order-id'>
            {placedOrder?.id}
          </p>
          <LabelDivider>{orderTotalText[language]}</LabelDivider>
          <p className={s.estimatedTotal} data-testid='placed-order-total'>
            {placedOrder?.total_sum}
          </p>
        </DialogContent>
        <DialogActions className={s.actions}>
          <Button aria-label={continueText[language]} data-prodantix='Close the order confirmation' onClick={handleCloseConfirmation} variant='contained'>
            {continueText[language]}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
